import Button from '@/components/Buttons/Button';
import Checkbox from '@/components/Checkbox/Checkbox';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import styled from '@emotion/styled';
import { DialogProps, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const DialogContent = styled.div`
    padding: 4rem;
`;

const Title = styled(Typography)`
    margin-bottom: 1.6rem;
`;

const CheckboxContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.bgLight};
    border: 1px solid ${({ theme }) => theme.colors.primary.main};
    border-radius: 8px;
    margin: 3.2rem 0;
    padding: 0 0.9rem;
    min-height: 5.2rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    cursor: pointer;
`;

const ButtonContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-end;
`;

const SubmitButton = styled(Button)`
    font-weight: 600;
    height: 4.4rem;
`;

const Link = styled.a`
    color: ${({ theme }) => theme.colors.links.main};
    text-decoration: none;

    &:hover {
        color: ${({ theme }) => theme.colors.links.main};
        text-decoration: none;
    }

    &:focus {
        outline: none;
    }
`;

interface Props extends DialogProps {
    onRemoveWatermark: () => void;
}

export default function AiAgreementModal({ onRemoveWatermark, ...dialogProps }: Props) {
    const [confirmationChecked, setConfirmationChecked] = useState(false);

    useEffect(() => {
        if (!dialogProps.open) {
            setConfirmationChecked(false);
        }
    }, [dialogProps.open]);

    function handleRemoveWatermark() {
        onRemoveWatermark();
        dialogProps.onClose?.({}, 'backdropClick');
    }

    return (
        <BootstrapDialog width="565px" {...dialogProps}>
            <DialogContent>
                <Title variant="h2">Remove AI Watermarks?</Title>
                <Typography variant="body1" lineHeight="21.86px" letterSpacing={0}>
                    At D-ID, we believe in the responsible use of Generative AI. The AI watermark ensures viewers are aware of the synthetic
                    nature of the content.
                    <br />
                    <br />
                    By removing the AI watermark, you agree to follow our{' '}
                    <Link target="_blank" href="https://www.d-id.com/ethics/">
                        AI Ethics Guidelines
                    </Link>
                    , ensuring transparency regarding the synthetic
                    <br />
                    nature of the content, avoiding harm, and promoting fairness.
                </Typography>
                <CheckboxContainer
                    onClick={() => {
                        setConfirmationChecked(prevChecked => !prevChecked);
                    }}>
                    <Checkbox outlined disableHover checked={confirmationChecked} />
                    <Typography variant="body1" fontWeight={500} lineHeight="20px">
                        I agree to use D-ID respectfully and responsibly
                    </Typography>
                </CheckboxContainer>
                <ButtonContainer>
                    <SubmitButton color="secondary" onClick={handleRemoveWatermark} disabled={!confirmationChecked}>
                        Remove watermark
                    </SubmitButton>
                </ButtonContainer>
            </DialogContent>
        </BootstrapDialog>
    );
}
